import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from '@mui/material';

function ErrorReportModal({ open, onClose, errors }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedErrors = useMemo(
    () => errors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [errors, page, rowsPerPage]
  );

  const handleDownload = () => {
    try {
      const csvHeader = ['Row Number', 'Error Messages'];
      const csvRows = errors.map((e) => [e.row, e.message]);
      const csvContent = [csvHeader, ...csvRows]
        .map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'error_report.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download CSV:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="error-report-dialog-title"
    >
      <DialogTitle id="error-report-dialog-title">Error Report</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          The following errors occurred during the operation:
        </Typography>
        <TableContainer component={Paper} sx={{ maxHeight: 400, width: '100%' }}>
          <Table stickyHeader aria-label="error report table">
            <TableHead>
              <TableRow>
                <TableCell><strong>Row Number</strong></TableCell>
                <TableCell><strong>Error Message</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedErrors.map((error) => (
                <TableRow key={`error-${error.row}`}>
                  <TableCell>{error.row}</TableCell>
                  <TableCell>{error.message}</TableCell>
                </TableRow>
              ))}
              {paginatedErrors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No errors to display.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {errors.length > rowsPerPage && (
          <TablePagination
            component="div"
            count={errors.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Rows per page:"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
        <Button
          onClick={handleDownload}
          color="secondary"
          variant="outlined"
          disabled={errors.length === 0}
        >
          Download CSV Report
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ErrorReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      row: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default React.memo(ErrorReportModal);
